import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { TableHeaderCell } from './TableHeaderCell'
import { ColumnMenuPopover } from './ColumnMenuPopover'
import { ColumnChooserDrawer } from './ColumnChooserDrawer'
import { TableFooterPagination } from './TableFooterPagination'
import { EnhancedCell } from './components/EnhancedCell'
import { formatCellValue, getCellValue } from './formatters'
import Loader from 'src/utils/Loader'
import {
  CatalogColumn,
  SelectedColumn,
  SortState,
  FilterState,
  PaginationState,
  DateRangeFilterValue,
  FilterValue,
} from './types'

type GroupedRowItem = Record<string, any>

type GroupedItem = {
  groupKey: string
  groupLabel: string
  items: GroupedRowItem[]
}

interface GroupedViewLiteProps {
  // Data
  data: GroupedItem[]
  loading: boolean

  // Columns
  catalogColumns: CatalogColumn[]
  selectedColumns: SelectedColumn[]
  onColumnsChange: (columns: SelectedColumn[]) => void
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void
  onPinColumn?: (field: string, position: 'left' | 'right' | null) => void
  getColumnByField: (field: string) => CatalogColumn | undefined

  // Sorting
  sortState: SortState | null
  onSortChange: (field: string, direction: 'asc' | 'desc') => void

  // Filtering
  filterState: FilterState
  onFilterChange: (filters: FilterState) => void

  // Pagination
  paginationState: PaginationState
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void

  // Actions
  onRefresh?: () => void
  onExportCSV?: () => void
  onResetColumns?: () => void

  // Platform/context
  platformId?: string
  channelId?: string
  mainStore?: boolean
  erpStore?: boolean
  tabsData?: any[]
  onLinkClick?: (rowData: any, rowIndex: number) => void
  onResyncClick?: (rowData: any) => void

  // Bulk actions / selection
  enableBulkActions?: boolean
  selectedAction?: string
  onSelectedActionChange?: (action: string) => void
  selectedCount?: number
  selectionEnabled?: boolean
  selectedRowIds?: Array<number | string>
  onSelectedRowIdsChange?: (ids: Array<number | string>) => void

  // Column config
  mandatoryColumns?: string[]
  onOpenColumnsConfig?: () => Promise<void> | void
  defaultVisibleFields?: string[]
}

type EnhancedCellType = 'text' | 'status' | 'productName' | 'price' | 'badge' | 'date'

const getEnhancedCellType = (columnType?: string): EnhancedCellType => {
  switch (columnType) {
    case 'status':
      return 'status'
    case 'productName':
      return 'productName'
    case 'price':
      return 'price'
    case 'badge':
      return 'badge'
    case 'date':
      return 'date'
    case 'number':
    case 'custom':
    case 'text':
    default:
      return 'text'
  }
}

export const GroupedViewLite: FC<GroupedViewLiteProps> = ({
  data,
  loading,
  catalogColumns,
  selectedColumns,
  onColumnsChange,
  onReorderColumns,
  onPinColumn,
  getColumnByField,
  sortState,
  onSortChange,
  filterState,
  onFilterChange,
  paginationState,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onExportCSV,
  onResetColumns,
  platformId,
  channelId,
  mainStore,
  erpStore,
  tabsData = [],
  onLinkClick,
  onResyncClick,
  enableBulkActions = false,
  selectedAction = '',
  onSelectedActionChange,
  selectedCount = 0,
  selectionEnabled = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  mandatoryColumns = ['name'],
  onOpenColumnsConfig,
  defaultVisibleFields = [],
}) => {
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [activeColumn, setActiveColumn] = useState<CatalogColumn | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [columnChooserOpen, setColumnChooserOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(filterState.globalSearch || '')
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const selectedIdSet = useMemo(() => new Set((selectedRowIds || []).map(String)), [selectedRowIds])
  const mandatorySet = useMemo(
    () => new Set((mandatoryColumns || []).filter(Boolean)),
    [mandatoryColumns]
  )

  const isDateRangeFilterValue = (value: FilterValue | ''): value is DateRangeFilterValue =>
    typeof value === 'object' && value !== null && ('from' in value || 'to' in value)

  const hasFilterValue = (value: FilterValue | '') => {
    if (isDateRangeFilterValue(value)) return Boolean(value.from || value.to)
    return value !== '' && value !== null && value !== undefined
  }

  useEffect(() => {
    setSearchQuery(filterState.globalSearch || '')
  }, [filterState.globalSearch])

  useEffect(() => {
    if (!tableScrollRef.current) return
    tableScrollRef.current.scrollLeft = 0
    tableScrollRef.current.scrollTop = 0
  }, [platformId, channelId])

  const handleOpenColumnMenu = useCallback(
    (column: CatalogColumn, anchorEl: HTMLElement) => {
      const sameColumn = activeColumn?.field === column.field
      const sameAnchor = menuAnchorEl === anchorEl

      if (columnMenuOpen && sameColumn && sameAnchor) {
        setColumnMenuOpen(false)
        setActiveColumn(null)
        setMenuAnchorEl(null)
        return
      }

      setActiveColumn(column)
      setMenuAnchorEl(anchorEl)
      setColumnMenuOpen(true)
    },
    [activeColumn, menuAnchorEl, columnMenuOpen]
  )

  const handleCloseColumnMenu = useCallback(() => {
    setColumnMenuOpen(false)
    setActiveColumn(null)
    setMenuAnchorEl(null)
  }, [])

  const handleSort = useCallback(
    (direction: 'asc' | 'desc') => {
      if (activeColumn) {
        onSortChange(activeColumn.field, direction)
      }
    },
    [activeColumn, onSortChange]
  )

  const handleColumnFilter = useCallback(
    (value: FilterValue | '', operator: string = 'contains') => {
      if (!activeColumn) return

      const existingFilter = filterState.columnFilters.find(
        (f) => f.field === activeColumn.field
      )
      if (!hasFilterValue(value) && !existingFilter) {
        return
      }

      const newFilters = filterState.columnFilters.filter(
        (f) => f.field !== activeColumn.field
      )

      if (hasFilterValue(value)) {
        newFilters.push({
          field: activeColumn.field,
          operator,
          value,
        })
      }

      onFilterChange({
        ...filterState,
        columnFilters: newFilters,
      })
    },
    [activeColumn, filterState, onFilterChange]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value)

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        onFilterChange({
          ...filterState,
          globalSearch: value,
        })
      }, 500)
    },
    [filterState, onFilterChange]
  )

  const handleAutosize = useCallback(() => {
    // placeholder to stay behavior-compatible with ProductTableLite
    // implement later if needed
  }, [])

  const handleAutosizeAll = useCallback(() => {
    // placeholder to stay behavior-compatible with ProductTableLite
    // implement later if needed
  }, [])

  const handleToggleColumn = useCallback(
    (field: string) => {
      const catalogColumn = getColumnByField(field)
      if (
        mandatorySet.has(field) ||
        catalogColumn?.fixed ||
        catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE'
      ) {
        return
      }

      const exists = selectedColumns.find((col) => col.field === field)

      if (exists) {
        const updated = selectedColumns
          .filter((col) => col.field !== field)
          .map((col, idx) => ({ ...col, order: idx }))
        onColumnsChange(updated)
      } else {
        const maxOrder =
          selectedColumns.length > 0
            ? Math.max(...selectedColumns.map((c) => c.order))
            : -1

        onColumnsChange([
          ...selectedColumns,
          {
            field,
            order: maxOrder + 1,
            width: catalogColumn?.width,
            pinned: null,
          },
        ])
      }
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  const handleToggleMultipleColumns = useCallback(
    (fields: string[]) => {
      const allowedFields = fields.filter((field) => {
        if (mandatorySet.has(field)) return false
        const col = getColumnByField(field)
        return !(col?.fixed || col?.allowVisibility === 'ALWAYS_VISIBLE')
      })

      if (allowedFields.length === 0) return

      const existingFields = new Set(selectedColumns.map((col) => col.field))
      const missingFields = allowedFields.filter((field) => !existingFields.has(field))
      if (missingFields.length === 0) return

      let maxOrder =
        selectedColumns.length > 0
          ? Math.max(...selectedColumns.map((c) => c.order))
          : -1

      const newColumns = missingFields.map((field) => {
        maxOrder += 1
        return {
          field,
          order: maxOrder,
          width: getColumnByField(field)?.width,
          pinned: null as 'left' | 'right' | null,
        }
      })

      onColumnsChange([...selectedColumns, ...newColumns])
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  const handleUncheckAllColumns = useCallback(() => {
    const defaultSet = new Set((defaultVisibleFields || []).filter(Boolean))
    const kept = selectedColumns.filter((col) => {
      const catalogColumn = getColumnByField(col.field)
      return (
        defaultSet.has(col.field) ||
        mandatorySet.has(col.field) ||
        Boolean(catalogColumn?.fixed) ||
        catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE'
      )
    })

    const normalized = kept
      .sort((a, b) => a.order - b.order)
      .map((col, idx) => ({ ...col, order: idx }))

    onColumnsChange(normalized)
  }, [selectedColumns, getColumnByField, mandatorySet, onColumnsChange, defaultVisibleFields])

  const getCurrentFilter = useCallback(() => {
    if (!activeColumn) return ''
    const filter = filterState.columnFilters.find((f) => f.field === activeColumn.field)
    return filter?.value ?? ''
  }, [activeColumn, filterState.columnFilters])

  const getCurrentOperator = useCallback(() => {
    if (!activeColumn) return 'contains'
    const filter = filterState.columnFilters.find((f) => f.field === activeColumn.field)
    return filter?.operator || activeColumn.filterOperators?.[0] || 'contains'
  }, [activeColumn, filterState.columnFilters])

  const formatHeaderLabel = useCallback((column: CatalogColumn) => {
    return (column.headerName || column.label || column.name || column.field || '').trim()
  }, [])

  const isMenuDisabledForColumn = useCallback((column: CatalogColumn) => {
    return !column.sortable && !column.filterable
  }, [])

  const hasActiveFilterForField = useCallback(
    (field: string) => {
      const filter = filterState.columnFilters.find((f) => f.field === field)
      return filter ? hasFilterValue(filter.value) : false
    },
    [filterState.columnFilters]
  )

  const shouldCenterAlign = useCallback((field: string) => {
    return [
      'isCreatedByCommercium',
      'total_variants',
      'total_available_quantity',
      'total_reserve_quantity',
      'status',
      'api_status',
    ].includes(field)
  }, [])

  const shouldWrapCell = useCallback((field: string) => {
    return ['name', 'title', 'description'].includes(field)
  }, [])

  const renderCellValue = useCallback(
    (row: any, column: CatalogColumn, rowIndex: number) => {
      if (column.field === 'name') {
        const productId = row?.id
        const name = row?.name || '-'

        return (
          <div className='d-flex flex-column'>
            <Link
              className='fw-bold text-info mb-0 text-decoration-none'
              to={`/products/${productId}`}
              onClick={() => onLinkClick?.(row, rowIndex)}
            >
              <span className='text-preserve-spacing'>{name}</span>
            </Link>
            {row?.sku && <small className='text-muted'>SKU: {row.sku}</small>}
          </div>
        )
      }

      const rawValue = getCellValue(row, column.field)

      return (
       <EnhancedCell
  value={formatCellValue(rawValue, column.type, (column as any)?.format)}
  type={getEnhancedCellType(column.type)}
  row={row}
/>
      )
    },
    [onLinkClick]
  )

  const organizedColumns = useMemo(() => {
    const sorted = [...selectedColumns].sort((a, b) => a.order - b.order)

    const pinnedLeft: SelectedColumn[] = []
    const unpinned: SelectedColumn[] = []
    const pinnedRight: SelectedColumn[] = []

    sorted.forEach((col) => {
      if (col.pinned === 'left') pinnedLeft.push(col)
      else if (col.pinned === 'right') pinnedRight.push(col)
      else unpinned.push(col)
    })

    return {
      pinnedLeft,
      unpinned,
      pinnedRight,
      all: [...pinnedLeft, ...unpinned, ...pinnedRight],
    }
  }, [selectedColumns])

  const renderedGroups = useMemo(() => {
    const globalSearch = (filterState.globalSearch || '').toLowerCase().trim()
    const columnFilters = filterState.columnFilters || []

    return (data || [])
      .map((group) => {
        let items = Array.isArray(group.items) ? [...group.items] : []

        if (globalSearch) {
          items = items.filter((row) =>
            JSON.stringify(row ?? {}).toLowerCase().includes(globalSearch)
          )
        }

        if (columnFilters.length > 0) {
          items = items.filter((row) => {
            return columnFilters.every((filter) => {
              const rowValue = getCellValue(row, filter.field)
              const compareValue = String(rowValue ?? '').toLowerCase()
              const filterValue = String(filter.value ?? '').toLowerCase().trim()

              if (!filterValue) return true
              return compareValue.includes(filterValue)
            })
          })
        }

        return {
          ...group,
          items,
        }
      })
      .filter((group) => group.items.length > 0)
  }, [data, filterState])

  useEffect(() => {
  console.log('🧠 RENDER DEBUG', {
    incomingDataLength: data?.length,
    firstGroup: data?.[0],

    filters: filterState,

    rendered: {
      renderedGroupsLength: renderedGroups?.length,
      firstRenderedGroup: renderedGroups?.[0],
    },

    visibleRowsCount: renderedGroups?.flatMap(g => g.items)?.length,
  })
}, [data, renderedGroups, filterState])

  const visibleRows = useMemo(
    () => renderedGroups.flatMap((group) => group.items),
    [renderedGroups]
  )

  const isRowSelected = useCallback(
    (row: any) => selectedIdSet.has(String(row?.id)),
    [selectedIdSet]
  )

  const toggleRowSelection = useCallback(
    (row: any) => {
      if (!onSelectedRowIdsChange) return

      const rowId = String(row?.id)
      const exists = selectedIdSet.has(rowId)

      if (exists) {
        onSelectedRowIdsChange((selectedRowIds || []).filter((id) => String(id) !== rowId))
      } else {
        onSelectedRowIdsChange([...(selectedRowIds || []), row?.id])
      }
    },
    [onSelectedRowIdsChange, selectedIdSet, selectedRowIds]
  )

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((row) => selectedIdSet.has(String(row?.id)))

  const toggleSelectAllVisible = useCallback(() => {
    if (!onSelectedRowIdsChange) return

    if (allVisibleSelected) {
      const visibleIds = new Set(visibleRows.map((row) => String(row?.id)))
      onSelectedRowIdsChange((selectedRowIds || []).filter((id) => !visibleIds.has(String(id))))
    } else {
      const merged = new Set((selectedRowIds || []).map(String))
      visibleRows.forEach((row) => merged.add(String(row?.id)))
      onSelectedRowIdsChange(Array.from(merged))
    }
  }, [allVisibleSelected, onSelectedRowIdsChange, selectedRowIds, visibleRows])

  const toggleGroupCollapse = useCallback((groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }, [])

  const getStickyStyle = (
    column: SelectedColumn,
    index: number,
    isHeader: boolean = false
  ): React.CSSProperties => {
    if (!column.pinned) return {}

    const baseZIndex = isHeader ? 20 : 10

    if (column.pinned === 'left') {
      let leftOffset = 0
      for (let i = 0; i < index; i++) {
        const prevCol = organizedColumns.pinnedLeft[i]
        if (prevCol) {
          const catalogCol = getColumnByField(prevCol.field)
          leftOffset += prevCol.width || catalogCol?.width || 150
        }
      }

      return {
        position: 'sticky',
        left: leftOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderRight:
          index === organizedColumns.pinnedLeft.length - 1
            ? '2px solid #e4e6ef'
            : undefined,
      }
    } else if (column.pinned === 'right') {
      let rightOffset = 0
      const rightIndex = organizedColumns.pinnedRight.findIndex(
        (c) => c.field === column.field
      )

      for (let i = rightIndex + 1; i < organizedColumns.pinnedRight.length; i++) {
        const nextCol = organizedColumns.pinnedRight[i]
        if (nextCol) {
          const catalogCol = getColumnByField(nextCol.field)
          rightOffset += nextCol.width || catalogCol?.width || 150
        }
      }

      return {
        position: 'sticky',
        right: rightOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderLeft: rightIndex === 0 ? '2px solid #e4e6ef' : undefined,
      }
    }

    return {}
  }
  

  return (
    <>
      <style>
        {`
          .flat-search-input {
            border: 1px solid #d1d3e0;
            border-radius: 6px;
            padding: 0.5rem 0.75rem 0.5rem 2.35rem;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            background: #ffffff;
          }
          .flat-search-input:focus {
            border-color: #3699ff;
            box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.1);
            outline: none;
          }
          .flat-btn {
            border-radius: 6px;
            padding: 0.45rem 0.9rem;
            font-weight: 600;
            font-size: 0.82rem;
            transition: all 0.2s ease;
            border: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }
          .flat-btn-primary {
            background: #3699ff;
            color: #ffffff;
          }
          .flat-btn-primary:hover {
            background: #187de4;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(54, 153, 255, 0.3);
          }
          .flat-btn-secondary {
            background: #f3f6f9;
            color: #7e8299;
          }
          .flat-btn-secondary:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn-icon {
            border-radius: 8px;
            padding: 0.65rem 0.75rem;
            background: #f3f6f9;
            color: #7e8299;
            border: none;
            transition: all 0.2s ease;
          }
          .flat-btn-icon:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none !important;
          }
          .listing-lite-table .table.table-row-bordered > thead > tr > th,
          .listing-lite-table .table.table-row-bordered > tbody > tr > td {
            border-bottom-color: rgba(0, 0, 0, 0.12) !important;
          }
          .listing-lite-table .table-scroll-container {
            max-height: 62vh;
            overflow: auto;
            position: relative;
          }
          .listing-lite-table .table-scroll-container thead th {
            position: sticky;
            top: 0;
            z-index: 12;
            background: #fff;
          }
          .listing-lite-table .group-row td {
            background: #f8f9fa !important;
            border-top: 2px solid #e4e6ef !important;
            border-bottom: 1px solid #e4e6ef !important;
          }
        `}
      </style>

      <div className='card listing-lite-table'>
        <div className='card-header border-0 py-3 px-4'>
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative'>
              <i
                className='bi bi-search position-absolute ms-3 text-muted'
                style={{ fontSize: '1rem' }}
              ></i>
              <input
                type='text'
                className='flat-search-input'
                placeholder='Search'
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ width: '200px' }}
              />
            </div>
          </div>

          <div className='card-toolbar'>
            <div className='d-flex flex-md-row flex-column justify-content-md-end align-items-md-center gap-2'>
              {enableBulkActions && (
                <select
                  className='form-select me-md-1'
                  aria-label='Select Action'
                  value={selectedAction}
                  onChange={(ev) => onSelectedActionChange?.(ev.target.value)}
                  style={{ minWidth: '145px', fontSize: '0.85rem' }}
                >
                  <option value={''}>Action</option>
                  <option value={'delist'} disabled={!selectedCount}>
                    Delist
                  </option>
                  <option value={'relist'} disabled={!selectedCount}>
                    Relist
                  </option>
                  <option value={'full_update'}>Full Update</option>
                </select>
              )}

              <button
                className='flat-btn flat-btn-secondary'
                onClick={async () => {
                  if (onOpenColumnsConfig) {
                    await onOpenColumnsConfig()
                  }
                  setColumnChooserOpen(true)
                }}
              >
                <i className='bi bi-columns-gap'></i>
                Columns
              </button>

              {onRefresh && (
                <button
                  className='flat-btn flat-btn-secondary'
                  onClick={onRefresh}
                  disabled={loading}
                >
                  <i className='bi bi-arrow-clockwise'></i>
                  Refresh
                </button>
              )}

              {onExportCSV && (
                <button
                  className='flat-btn flat-btn-secondary'
                  onClick={onExportCSV}
                  disabled={loading}
                >
                  <i className='bi bi-download'></i>
                  Export CSV
                </button>
              )}

              {onResetColumns && (
                <button
                  className='flat-btn flat-btn-secondary'
                  onClick={onResetColumns}
                  disabled={loading}
                  title='Reset to default columns'
                >
                  <i className='bi bi-arrow-repeat'></i>
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        <div className='card-body py-0 px-0'>
          <div ref={tableScrollRef} className='table-responsive table-scroll-container'>
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-0 mb-0'>
              <thead>
                <tr className='fw-bold text-muted'>
                  {selectionEnabled && (
                    <th
                      className='text-center'
                      style={{
                        minWidth: 54,
                        width: 54,
                        padding: '12px 8px',
                        verticalAlign: 'middle',
                        backgroundColor: '#fff',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                      />
                    </th>
                  )}

                  {organizedColumns.all.map((selectedCol, idx) => {
                    const column = getColumnByField(selectedCol.field)
                    if (!column) return null

                    const pinIndex =
                      selectedCol.pinned === 'left'
                        ? organizedColumns.pinnedLeft.findIndex(
                            (c) => c.field === selectedCol.field
                          )
                        : selectedCol.pinned === 'right'
                        ? organizedColumns.pinnedRight.findIndex(
                            (c) => c.field === selectedCol.field
                          )
                        : idx

                    return (
                      <th
                        key={selectedCol.field}
                        style={{
                          minWidth: selectedCol.width || column.width || 150,
                          width: selectedCol.width || column.width || 150,
                          padding: '12px 16px',
                          verticalAlign: 'middle',
                          ...getStickyStyle(selectedCol, pinIndex, true),
                        }}
                      >
                        <TableHeaderCell
                          column={column}
                          sortState={sortState}
                          onOpenMenu={handleOpenColumnMenu}
                          displayLabel={formatHeaderLabel(column)}
                          menuDisabled={isMenuDisabledForColumn(column)}
                          hasActiveFilter={hasActiveFilterForField(column.field)}
                        />
                      </th>
                    )
                  })}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={organizedColumns.all.length + (selectionEnabled ? 1 : 0)}
                      className='text-center py-10'
                    >
                      <Loader variant="table" />
                    </td>
                  </tr>
                ) : renderedGroups.length === 0 ? (
                  <tr>
                    <td
                      colSpan={organizedColumns.all.length + (selectionEnabled ? 1 : 0)}
                      className='text-center py-10 text-muted'
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  renderedGroups.map((group) => {
                    const isCollapsed = Boolean(collapsedGroups[group.groupKey])

                    return (
                      <React.Fragment key={group.groupKey}>
                        <tr className='group-row'>
                          <td
                            colSpan={organizedColumns.all.length + (selectionEnabled ? 1 : 0)}
                            className='fw-bold'
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleGroupCollapse(group.groupKey)}
                          >
                            <div className='d-flex align-items-center justify-content-between'>
                              <div className='d-flex align-items-center'>
                                <i
                                  className={`bi ${
                                    isCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'
                                  } me-2`}
                                />
                                <span>{group.groupLabel}</span>
                              </div>

                              <span className='badge badge-light-primary'>
                                {group.items.length}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {!isCollapsed &&
                          group.items.map((row, rowIndex) => (
                            <tr key={row?.id || `${group.groupKey}-${rowIndex}`}>
                              {selectionEnabled && (
                                <td
                                  className='text-center'
                                  style={{
                                    minWidth: 54,
                                    width: 54,
                                    padding: '12px 8px',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <input
                                    type='checkbox'
                                    checked={isRowSelected(row)}
                                    onChange={() => toggleRowSelection(row)}
                                  />
                                </td>
                              )}

                              {organizedColumns.all.map((selectedCol, idx) => {
                                const column = getColumnByField(selectedCol.field)
                                if (!column) return null

                                const pinIndex =
                                  selectedCol.pinned === 'left'
                                    ? organizedColumns.pinnedLeft.findIndex(
                                        (c) => c.field === selectedCol.field
                                      )
                                    : selectedCol.pinned === 'right'
                                    ? organizedColumns.pinnedRight.findIndex(
                                        (c) => c.field === selectedCol.field
                                      )
                                    : idx

                                return (
                                  <td
                                    key={`${row?.id || rowIndex}-${selectedCol.field}`}
                                    className={`text-dark fw-normal ${
                                      shouldCenterAlign(column.field) ? 'text-center' : ''
                                    }`}
                                    style={{
                                      whiteSpace: shouldWrapCell(column.field)
                                        ? 'normal'
                                        : 'nowrap',
                                      wordBreak: shouldWrapCell(column.field)
                                        ? 'break-word'
                                        : 'normal',
                                      maxWidth: shouldWrapCell(column.field) ? 420 : 240,
                                      overflow: 'hidden',
                                      textOverflow: shouldWrapCell(column.field)
                                        ? 'clip'
                                        : 'ellipsis',
                                      minWidth: selectedCol.width || column.width || 150,
                                      width: selectedCol.width || column.width || 150,
                                      padding: '12px 16px',
                                      verticalAlign: 'middle',
                                      ...getStickyStyle(selectedCol, pinIndex, false),
                                    }}
                                  >
                                    {renderCellValue(row, column, rowIndex)}
                                  </td>
                                )
                              })}
                            </tr>
                          ))}
                      </React.Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='card-footer border-0 pt-3 pb-4 px-4'>
          <TableFooterPagination
            paginationState={paginationState}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      </div>

      {activeColumn && (
        <ColumnMenuPopover
          column={activeColumn}
          isOpen={columnMenuOpen}
          onClose={handleCloseColumnMenu}
          onSortAsc={() => handleSort('asc')}
          onSortDesc={() => handleSort('desc')}
          onAutosize={handleAutosize}
          onAutosizeAll={handleAutosizeAll}
          onFilterChange={handleColumnFilter}
          currentFilter={getCurrentFilter()}
          currentOperator={getCurrentOperator()}
          anchorEl={menuAnchorEl}
        />
      )}

      <ColumnChooserDrawer
        isOpen={columnChooserOpen}
        onClose={() => setColumnChooserOpen(false)}
        catalogColumns={catalogColumns}
        selectedColumns={selectedColumns}
        onToggleColumn={handleToggleColumn}
        onToggleMultipleColumns={handleToggleMultipleColumns}
        onUncheckAllColumns={handleUncheckAllColumns}
        onReorderColumns={onReorderColumns}
        onPinColumn={onPinColumn}
        getColumnByField={getColumnByField}
        mandatoryColumns={mandatoryColumns}
      />
    </>
  )
}

export default GroupedViewLite